/**
 * The audio companion channel — the wire contract between the player's tab, the sidecar and
 * the game server. Phase 1 of `docs/design/audio-companion-plan.md` §7: the socket, and nothing that
 * makes a noise.
 *
 * WHAT IS HERE. Enough to open a socket, prove who is on the other end of it, agree on a
 * protocol generation, keep it alive, and measure the two clocks against each other. No areas,
 * no `sync` seeking, no volume categories, no alert payloads. {@link AudioServerMessage} is a
 * union with a seam for those (see {@link AudioMessageType}), and Phase 2 widens it.
 *
 * THREE PARTIES, TWO OF WHICH DIAL:
 *
 *   player's tab ──wss──▶ SIDECAR ◀──wss── game server
 *
 * Both arrows point INWARD, which is the property the whole deployment rests on: the game
 * server opens no inbound port, so it is the WebSocket *client* here exactly as it is the HTTP
 * client in `EditorHttpClient`. The sidecar is the only listener. Nothing dials the mod, and
 * nothing ever will — inverting this would mean a public address, a certificate and a firewall
 * hole on a Minecraft host.
 *
 * THE THREE PARTIES DO NOT HAVE EQUAL RIGHTS TO A CLOCK. The SIDECAR mints every timestamp on this
 * wire and stamps on receipt; the BROWSER corrects against it, because the browser is the end that
 * seeks audio; the GAME SERVER holds no clock at all and sends no probe, because measuring one would
 * mean an application frame every few seconds on the process whose tick time is scarcest. So
 * {@link AudioPingMessage} is a browser frame in practice even though the wire would allow either
 * peer to send one, and the `serverTimeMs`/`pingIntervalMs` pair on {@link AudioHelloMessage} exists
 * for the browser — the mod's `AudioWire.Hello` deliberately does not even model them. The cost is
 * that a mod-originated event is dated a few milliseconds after it happened; see `web/backend/src/audio.ts`.
 *
 * WHY THE CREDENTIAL TRAVELS IN `Sec-WebSocket-Protocol`. A browser's `WebSocket` constructor
 * takes a URL and a subprotocol list and nothing else — it cannot set an `Authorization`
 * header. The alternatives are a query parameter (which lands in every access log, every
 * `Referer` and every proxy trace — the same reason the login token travels in the URL
 * *fragment*, see `TOKEN_FRAGMENT_KEY`) or a first-frame login after the socket is already
 * open (which means an unauthenticated socket exists, however briefly, and needs its own
 * timeout and its own cap). The subprotocol is a request *header*, it is not logged by
 * default, and it is checked before a single frame is exchanged. The game server, which is not
 * a browser, uses the ordinary `Authorization: Bearer` it already uses everywhere else; the
 * sidecar accepts either transport and the credential itself decides which peer it is.
 */
import { isUuidText } from './grants';
import type { UuidText } from './views';

/**
 * The protocol generation. Bumped when a frame changes shape in a way an older tab would
 * misread; NOT bumped for a new message type, which an older tab ignores by construction.
 *
 * THIS IS THE "TELL A STALE TAB TO RELOAD" MECHANISM. A player leaves the companion tab open
 * for days. We deploy. Their JavaScript is now older than the sidecar, and the failure mode we
 * are buying our way out of is the quiet one: a tab that stays connected, misparses an area
 * definition and plays the wrong thing forever, with nothing in any log. So the version is
 * declared by the CLIENT in the handshake (`dcaudio.v1`) and checked by the SIDECAR, which is
 * the only end that can be trusted to check — an old tab cannot be relied on to notice it is
 * old. A mismatch is {@link AUDIO_CLOSE_PROTOCOL_VERSION}, whose whole meaning is "reload".
 *
 * One constant, imported by the SPA and by the sidecar. The mod mirrors it in
 * `AudioWire.VERSION` and refuses a hello that disagrees, the same way `EditorWire.VERSION` is
 * mirrored — a copy is unavoidable across languages, so it is a single named copy rather than
 * a literal at each use.
 */
export const AUDIO_PROTOCOL_VERSION = 1;

/** The sidecar route the socket upgrades on. Mounted by `attachAudioChannel`. */
export const AUDIO_PATH = '/v1/audio';

/**
 * The subprotocol token declaring which protocol generation the client speaks:
 * `dcaudio.v1`. Always sent, by both peer kinds.
 */
export function audioVersionSubprotocol(version: number = AUDIO_PROTOCOL_VERSION): string {
  return `dcaudio.v${version}`;
}

/** Prefix of the credential-bearing subprotocol token. See the header for why it is here. */
export const AUDIO_TOKEN_SUBPROTOCOL_PREFIX = 'dcaudio.token.';

/**
 * `dcaudio.token.<credential>`.
 *
 * RFC 6455 subprotocol names are HTTP tokens, so the value may not contain a space, a comma or
 * a quote. A compact-serialisation JWT is base64url plus two dots and a hex server token is
 * hex, so both fit with nothing to escape — and {@link parseAudioSubprotocols} refuses
 * anything that does not, rather than trying to decode an encoding we never defined.
 */
export function audioTokenSubprotocol(credential: string): string {
  return `${AUDIO_TOKEN_SUBPROTOCOL_PREFIX}${credential}`;
}

/** What the sidecar echoes back as the negotiated subprotocol on a socket it accepted. */
export function audioAcceptedSubprotocol(): string {
  return audioVersionSubprotocol(AUDIO_PROTOCOL_VERSION);
}

/** Characters RFC 6455 allows in a subprotocol name (RFC 7230 `token`). */
const SUBPROTOCOL_TOKEN = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;

/** Longest credential accepted in a subprotocol token. A JWT with a uuid claim is ~400 bytes. */
export const AUDIO_MAX_CREDENTIAL_LENGTH = 4096;

export interface AudioHandshake {
  /**
   * The protocol generation the client declared, or `null` when it declared none or declared
   * two. Null is a refusal, never a default: a client that cannot say what it speaks is a
   * client we cannot tell apart from a stale one.
   */
  readonly version: number | null;
  /** The credential from the subprotocol list, or `null` when it carried none. */
  readonly credential: string | null;
}

/**
 * Reads the `Sec-WebSocket-Protocol` list, which arrives as one comma-separated header value
 * (or several, which `ws` and Node both join with `, `).
 *
 * Deliberately total and deliberately quiet: an unknown token is IGNORED rather than refused,
 * because a future generation may add one and this function is the oldest code in the path. A
 * *second* version token is not ignored — it is `version: null` — because two declarations
 * mean the client does not know what it speaks, and picking one for it is how a stale tab
 * survives the check it exists to fail.
 */
export function parseAudioSubprotocols(header: string | null | undefined): AudioHandshake {
  if (typeof header !== 'string') return { version: null, credential: null };
  const tokens = header
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);

  let version: number | null = null;
  let versions = 0;
  let credential: string | null = null;
  let credentials = 0;

  for (const token of tokens) {
    if (!SUBPROTOCOL_TOKEN.test(token)) continue;
    const asVersion = /^dcaudio\.v(\d{1,4})$/.exec(token);
    if (asVersion !== null) {
      versions += 1;
      version = Number(asVersion[1]);
      continue;
    }
    if (token.startsWith(AUDIO_TOKEN_SUBPROTOCOL_PREFIX)) {
      credentials += 1;
      credential = token.slice(AUDIO_TOKEN_SUBPROTOCOL_PREFIX.length);
    }
  }

  return {
    version: versions === 1 ? version : null,
    credential: credentials === 1 && credential !== null && credential.length > 0 && credential.length <= AUDIO_MAX_CREDENTIAL_LENGTH ? credential : null,
  };
}

// ------------------------------------------------------------------------------- peers

/**
 * WHO IS ON THE SOCKET. Two kinds, and the difference is not a permission level, it is which
 * direction data flows: a `player` receives what happens to exactly one Minecraft account, and
 * a `server` reports what happened. Neither can do the other's job.
 */
export type AudioPeerKind = 'player' | 'server';

/**
 * A socket bound to one Minecraft account.
 *
 * `playerUuid` COMES OFF THE VERIFIED TOKEN'S `uuid` CLAIM AND FROM NOWHERE ELSE — never a
 * query parameter, never a first frame, never anything the tab can choose. It is the whole of
 * the authorisation model for this channel: a socket receives an event if and only if the
 * event names this uuid. There is no subscribe verb, no room id and no filter the client can
 * set, precisely so that there is nothing to get wrong later. Compare the read plane, which
 * reaches the same conclusion through Postgres RLS on the same claim.
 */
export interface AudioPlayerPeer {
  readonly kind: 'player';
  readonly playerUuid: UuidText;
}

/** The game server's single persistent socket, authenticated with the shared `serverToken`. */
export interface AudioServerPeer {
  readonly kind: 'server';
}

export type AudioPeer = AudioPlayerPeer | AudioServerPeer;

// ---------------------------------------------------------------------------- messages

/**
 * Every frame type this generation defines, server→client and client→server.
 *
 * THE SEAM THE CLOSED BETA ARRIVED THROUGH. Plan §9.3 added `catalog`, `context` and `cue` towards
 * the tab, plus the game server's own frames ({@link AudioGameFrameType}). Adding a type is NOT a
 * version bump: an unknown `type` is dropped by both ends (see {@link isAudioServerMessage}), so a tab
 * from before a frame existed stays silent rather than breaking. Changing a field on a type that
 * already ships IS a version bump.
 */
export type AudioMessageType = 'hello' | 'ping' | 'pong' | 'refused' | 'catalog' | 'context' | 'cue';

/**
 * The first frame the sidecar sends on a socket it accepted, and the acknowledgement the whole
 * handshake exists to produce.
 *
 * It carries the sidecar's own version even though the client already declared its own and was
 * checked against it, because the check happens at the handshake where a browser can see
 * neither a body nor a status. A tab that somehow reaches an accepted socket with a mismatch
 * can still compare and say so.
 */
export interface AudioHelloMessage {
  readonly type: 'hello';
  /** The SIDECAR's generation. Equal to the client's, or the socket would not be open. */
  readonly version: number;
  readonly peer: AudioPeerKind;
  /** Present if and only if `peer` is `player`. Echoed so the tab can prove which account it got. */
  readonly playerUuid?: UuidText;
  /**
   * Epoch MILLISECONDS on the sidecar's clock — the first clock sample, free with the hello.
   *
   * For the BROWSER. The mod ignores it and its `AudioWire.Hello` does not model it, because the mod
   * holds no clock; it is still sent to both peers because one hello for every peer is simpler than
   * two, and an unread field costs the mod nothing.
   */
  readonly serverTimeMs: number;
  /**
   * How often the client should send {@link AudioPingMessage}. See {@link AUDIO_PING_INTERVAL_MS}.
   * Also for the browser: the mod sends no probe, so it has no cadence to be advised of.
   */
  readonly pingIntervalMs: number;
}

/**
 * client→server clock probe, sent by the BROWSER and by nothing else. `id` is the client's own
 * counter, echoed back so a reply can be matched to its request even out of order; `clientTimeMs`
 * is the client's clock at send.
 *
 * THE GAME SERVER DOES NOT SEND THIS. It has no clock to correct and nothing to seek: the sidecar
 * stamps mod-originated events on receipt, so the mod's side of the channel is write-only apart
 * from its hello. A probe from the mod would have been a JSON encode and a socket write every five
 * seconds on the process least able to afford one — see `AudioSocketClient`'s tick-cost budget.
 *
 * THIS IS NOT A KEEPALIVE, and the Phase 0 spike is why. A hidden tab that is playing audio
 * holds 4 Hz timers, but a hidden *silent* tab falls to 0.01 Hz — so a liveness check built on
 * this frame would kill exactly the socket of a player who has not enabled audio yet.
 * Liveness is the WebSocket protocol's own ping/pong, which a browser answers from its network
 * stack and not from JavaScript, so throttling cannot touch it. See `AUDIO_LIVENESS_*`.
 */
export interface AudioPingMessage {
  readonly type: 'ping';
  readonly id: number;
  readonly clientTimeMs: number;
}

/** server→client reply to a {@link AudioPingMessage}. Feeds {@link import('./audioclock').AudioClock}. */
export interface AudioPongMessage {
  readonly type: 'pong';
  readonly id: number;
  /** Echoed unchanged from the ping — the client's T0. */
  readonly clientTimeMs: number;
  /** The sidecar's clock when it handled the ping. */
  readonly serverTimeMs: number;
}

/**
 * The last frame on a socket the sidecar is closing for a reason the far end can act on.
 *
 * Sent BEFORE the close frame because a browser gets a usable `code` and `reason` on a close
 * event but nothing else — no body, no status — and because a `reason` is capped at 123 bytes.
 * A refusal worth explaining gets a frame; the close code is the machine-readable half.
 */
export interface AudioRefusedMessage {
  readonly type: 'refused';
  readonly code: AudioRefusalCode;
  /** The sidecar's generation, so a stale tab can log what it should have been. */
  readonly serverVersion: number;
  readonly detail?: string;
}

/**
 * sidecar→tab: the areas and cues the game server configured (plan §9.3, §9.4). The SAME body the
 * game server sent, normalised by {@link normaliseAudioCatalog} on the sidecar, cached, and re-sent
 * to every tab on connect and on change. The tab normalises it again on receipt, because a tab is
 * the oldest code in the path the day a newer sidecar forwards a field this build does not know.
 */
export interface AudioCatalogMessage extends AudioCatalog {
  readonly type: 'catalog';
}

/**
 * sidecar→tab: where this tab's player is, as far as audio cares (plan §9.3).
 *
 * `context` is NULL when the sidecar holds nothing for the player — they have not joined since the
 * game server last connected, or they left. `online` is the half the tab acts on: false is silence,
 * whatever the last context said. `serverTimeMs` is the sidecar's clock when it PUBLISHED this frame
 * (it stamps on receipt, plan §4); `instance` is present while the player is a member of a shared
 * playback (§9.9), and carries the timeline every member seeks against.
 */
export interface AudioContextMessage {
  readonly type: 'context';
  readonly context: PlayerAudioContext | null;
  readonly online: boolean;
  readonly serverTimeMs: number;
  readonly instance?: AudioInstanceRef;
}

/** sidecar→tab: a one-shot event happened to this player. Never cached, never replayed (§4). */
export interface AudioCueMessage {
  readonly type: 'cue';
  readonly cue: string;
  readonly serverTimeMs: number;
}

export type AudioServerMessage =
  | AudioHelloMessage
  | AudioPongMessage
  | AudioRefusedMessage
  | AudioCatalogMessage
  | AudioContextMessage
  | AudioCueMessage;
export type AudioClientMessage = AudioPingMessage;

// ------------------------------------------------------------- the §9 domain, on the wire

/**
 * The kinds of battle the mod reports (plan §9.3). The list the shipped areas are written against —
 * NOT a closed set on the wire: a context naming a kind this build has never heard of is kept (a
 * newer mod may know about raids), it simply matches no area that names kinds, and still matches an
 * area whose trigger names none ({@link AreaTrigger}).
 */
export const BATTLE_KINDS = ['wild', 'trainer', 'player', 'legendary', 'shiny', 'boss', 'tower'] as const;
export type BattleKind = (typeof BATTLE_KINDS)[number];

/**
 * An area's category, and therefore which volume slider it answers to (§9.5): `battle` → battle, `vehicle` →
 * vehicle, the rest → music.
 *
 * `vehicle` IS ALSO A LAYER OF ITS OWN. The tab plays two areas at once: the best match among every other
 * category (the main layer, §9.4, shared instances and all), and — at the same time, over it — the best match
 * among `vehicle` areas alone (the sound of the ride the player sits on). See `audioplay.ts`'s `layerOf`.
 */
export const AREA_CATEGORIES = ['ambient', 'music', 'battle', 'vehicle'] as const;
export type AreaCategory = (typeof AREA_CATEGORIES)[number];

/** Which slider a cue plays under (§9.5). Absent on the wire is `alerts`, as every cue before this field was. */
export const CUE_CATEGORIES = ['alerts', 'vehicle'] as const;
export type CueCategory = (typeof CUE_CATEGORIES)[number];

/** What a `ride` trigger may ask of the ride's motion. Absent is either. */
export const RIDE_MOTIONS = ['moving', 'standing'] as const;
export type RideMotion = (typeof RIDE_MOTIONS)[number];

/** What a `ride` trigger may ask of the water. Absent is either. */
export const RIDE_WATERS = ['in', 'out'] as const;
export type RideWater = (typeof RIDE_WATERS)[number];

/**
 * How an area walks its track list.
 *
 * * `loop` — the first track, repeated for as long as the area plays. Extra tracks are ignored.
 * * `sequence` — every track in order, then round again.
 * * `shuffle` — every track once per round in a fresh random order, never the same track twice in a
 *   row across a round boundary. A shared instance draws the order from its id, so members agree.
 * * `once` — every track in order, then silence (a ride's score, §9.9). The area still "plays":
 *   nothing below it in priority takes over just because the music stopped.
 */
export const AREA_ORDERS = ['loop', 'sequence', 'shuffle', 'once'] as const;
export type AreaOrder = (typeof AREA_ORDERS)[number];

/**
 * When an area wants to play (plan §9.4). One field is matched against the context per kind. The world,
 * district, base and battle are each something the mod knows at an event it already handles; the biome and
 * the ride are sampled once a second on the server (see {@link PlayerAudioContext}).
 *
 * `battle.battles` EMPTY MEANS ANY BATTLE. An area that can never play is not a useful thing to be
 * able to write, and "the theme for every fight we did not write a theme for" is. `ride.rides` empty is
 * any ride, for the same reason: the sound of every vehicle nobody wrote one for.
 *
 * `biome.biomes` EMPTY MATCHES NOTHING — there is no "any biome" (that is `default`). The mod refuses an
 * empty list at load, so in practice none arrives; one that does is kept, and never plays.
 *
 * `ride.motion` and `ride.water` ABSENT MEAN EITHER. `moving` asks for `ride.moving`, `standing` for its
 * absence; `in` asks for `ride.water`, `out` for its absence.
 */
export type AreaTrigger =
  | { readonly kind: 'default' }
  | { readonly kind: 'dimension'; readonly id: string }
  | { readonly kind: 'district'; readonly id: string }
  | { readonly kind: 'base' }
  | { readonly kind: 'battle'; readonly battles: readonly string[] }
  | { readonly kind: 'biome'; readonly biomes: readonly string[] }
  | { readonly kind: 'ride'; readonly rides: readonly string[]; readonly motion?: RideMotion; readonly water?: RideWater };

export const AREA_TRIGGER_KINDS = ['default', 'dimension', 'district', 'base', 'battle', 'biome', 'ride'] as const;

/** One area: what plays, when, and how (plan §9.4). Track ids are keys of the SPA's manifest. */
export interface AreaDef {
  readonly id: string;
  /** MiniMessage. The tab renders the colours it can and shows the text either way. */
  readonly displayName: string;
  readonly category: AreaCategory;
  /** Highest wins. Ties go to catalog order — the earlier area. */
  readonly priority: number;
  readonly trigger: AreaTrigger;
  readonly tracks: readonly string[];
  /** Crossfade into this area, in milliseconds. Clamped to {@link AUDIO_MAX_FADE_MS}. */
  readonly fadeMs: number;
  /** 0..1, multiplied into the category slider and the track's loudness correction. */
  readonly volume: number;
  readonly order: AreaOrder;
}

/**
 * A one-shot sound the mod names by id (`mail`, `catch`, `vehicle_depart`…), played under the slider of its
 * `category`: `alerts` when absent, as every cue was before the field existed, or `vehicle` for the sounds of
 * the ride a player sits on.
 */
export interface CueDef {
  readonly id: string;
  readonly track: string;
  readonly volume: number;
  readonly category?: CueCategory;
}

/**
 * The game server's audio configuration, as the tab needs it.
 *
 * `lang` is the language of the TEXT the server shows players (§9.8) — the language a voiced line
 * must match. It is the server's decision, never the browser's.
 */
export interface AudioCatalog {
  /**
   * Which configuration this is — 16 lower-case hex characters, the first 64 bits of SHA-256 over the
   * encoded `lang`, `areas` and `cues` (plan §9.3 "Wire rules"), so the same config is the same
   * revision across restarts and a re-sent catalog can be recognised as a repeat.
   */
  readonly revision: string;
  readonly lang: string;
  readonly areas: readonly AreaDef[];
  readonly cues: readonly CueDef[];
}

/**
 * Where a player is, as far as audio cares (plan §9.3). `battle` is a string rather than a
 * {@link BattleKind} for the forward-compatibility reason {@link BATTLE_KINDS} gives. `lang` is
 * RESERVED (§9.8): when present it overrides the catalog's language for this player.
 *
 * `biome` and `ride` are SAMPLED, once a second on the server, so each changes at most once a second per
 * player. `biome` (`namespace:path`) is sent only when some `biome` trigger of the current catalog names the
 * biome at the player's feet, so walking between biomes no area cares about sends nothing at all. `ride` is
 * present while the player sits on a seat of a ride.
 */
export interface PlayerAudioContext {
  readonly dimension: string;
  readonly district?: string;
  readonly base?: boolean;
  readonly battle?: string;
  readonly lang?: string;
  readonly biome?: string;
  readonly ride?: AudioRide;
}

/**
 * The ride a player sits on (the Ride mod). `id` is the ride's id (`cd-safari-ring`), or `""` when the mod
 * cannot tell which ride it is — still a ride, so a trigger for any ride matches it and one naming rides does
 * not. The two flags are only ever `true`, or absent.
 */
export interface AudioRide {
  readonly id: string;
  readonly moving?: true;
  readonly water?: true;
}

/**
 * A shared playback this player is a member of (plan §9.9). `startedAtMs` is the SIDECAR's clock the
 * first time it saw `id`, kept for the instance's life — so a late joiner and a reconnecting tab seek
 * against the same instant as everyone else.
 */
export interface AudioInstanceRef {
  readonly id: string;
  readonly area: string;
  readonly startedAtMs: number;
}

// ------------------------------------------------------------- the game server's frames

/**
 * Every frame the GAME SERVER sends (plan §9.3 "Mod → sidecar"). Event-driven only — §4's budget:
 * none of these is periodic, and the sidecar answers none of them, so the mod's socket stays a
 * write-only pipe apart from its hello.
 */
export type AudioGameFrameType = 'catalog' | 'context' | 'cue' | 'leave' | 'instance' | 'instance_end';

export interface AudioCatalogFrame extends AudioCatalog {
  readonly type: 'catalog';
}
export interface AudioContextFrame {
  readonly type: 'context';
  readonly player: UuidText;
  readonly context: PlayerAudioContext;
}
export interface AudioCueFrame {
  readonly type: 'cue';
  readonly player: UuidText;
  readonly cue: string;
}
export interface AudioLeaveFrame {
  readonly type: 'leave';
  readonly player: UuidText;
}
/**
 * A shared playback started, or its members changed (§9.9). MEMBERSHIP, NEVER TIME: the mod holds
 * no clock (§4), so the sidecar stamps the start. A player names at most one instance at a time;
 * the newest frame naming them wins.
 */
export interface AudioInstanceFrame {
  readonly type: 'instance';
  readonly id: string;
  readonly area: string;
  readonly players: readonly UuidText[];
}
export interface AudioInstanceEndFrame {
  readonly type: 'instance_end';
  readonly id: string;
}

export type AudioGameFrame =
  | AudioCatalogFrame
  | AudioContextFrame
  | AudioCueFrame
  | AudioLeaveFrame
  | AudioInstanceFrame
  | AudioInstanceEndFrame;

/**
 * Stable machine-readable refusals. Nothing branches on prose; the SPA maps these to wording
 * exactly as it does {@link import('./link').LinkErrorCode}.
 */
export type AudioRefusalCode =
  /** No credential, or one whose signature/expiry did not check out. */
  | 'unauthorised'
  /**
   * A VALID token of the wrong tier. `unlinked` (no Minecraft account is bound, so there is no
   * stream to bind to) and the three staff tiers (`viewer`/`editor`/`admin` are a moderation
   * plane over *other* people's rows; this channel is one person's own soundtrack and a staff
   * token names no person). Distinct from `unauthorised` because signing in again will not fix
   * it, and the remedy — run `/link` in game — is one the reader can act on.
   */
  | 'forbidden_tier'
  /** The client declared a generation this sidecar does not speak. Means: RELOAD. */
  | 'protocol_version'
  /** Another socket for the same player took this one's place. See {@link AUDIO_MAX_SOCKETS_PER_PLAYER}. */
  | 'superseded'
  /** The sidecar is at its global socket ceiling. Transient; back off and retry. */
  | 'too_many_sockets'
  /** A frame that was not JSON, was over the cap, or was not a message this generation defines. */
  | 'bad_frame'
  /** The sidecar is shutting down. Reconnect. */
  | 'going_away';

/**
 * Close codes, in the 4000–4999 range RFC 6455 reserves for the application.
 *
 * They exist because a browser's close event is the ONLY channel a refused `WebSocket` has: no
 * status line, no body, no headers. A tab that is told `4009` knows to reload; a tab that gets
 * a bare `1006` knows nothing and will retry forever. Numbered loosely after the HTTP status
 * they rhyme with (401/403/409/429) so an operator reading a log can guess right.
 */
export const AUDIO_CLOSE_UNAUTHORISED = 4001;
export const AUDIO_CLOSE_FORBIDDEN_TIER = 4003;
export const AUDIO_CLOSE_SUPERSEDED = 4008;
export const AUDIO_CLOSE_PROTOCOL_VERSION = 4009;
export const AUDIO_CLOSE_TOO_MANY_SOCKETS = 4029;
export const AUDIO_CLOSE_BAD_FRAME = 4400;
export const AUDIO_CLOSE_GOING_AWAY = 4503;

/** The one mapping from a refusal to its close code. Total, so a new code cannot be forgotten. */
export const AUDIO_CLOSE_CODE_FOR: Readonly<Record<AudioRefusalCode, number>> = {
  unauthorised: AUDIO_CLOSE_UNAUTHORISED,
  forbidden_tier: AUDIO_CLOSE_FORBIDDEN_TIER,
  superseded: AUDIO_CLOSE_SUPERSEDED,
  protocol_version: AUDIO_CLOSE_PROTOCOL_VERSION,
  too_many_sockets: AUDIO_CLOSE_TOO_MANY_SOCKETS,
  bad_frame: AUDIO_CLOSE_BAD_FRAME,
  going_away: AUDIO_CLOSE_GOING_AWAY,
};

// -------------------------------------------------------------------------------- caps

/**
 * How often a client sends a clock probe. Craftventure's was 5 s and that is the right order:
 * a handful of samples inside the first half-minute is what {@link
 * import('./audioclock').AUDIO_CLOCK_WINDOW} needs to have a median worth taking.
 */
export const AUDIO_PING_INTERVAL_MS = 5_000;

/**
 * How often the SIDECAR sends a protocol-level ping. One interval is also the grace: a socket
 * that has not answered by the next round is terminated, so a dead socket is evicted in at most
 * two intervals and there is no second constant pretending to be a separate timer.
 *
 * Protocol-level, not {@link AudioPingMessage}, and that is a direct consequence of the Phase 0
 * spike: a hidden tab that is not playing audio gets timers at 0.01 Hz, so a JavaScript-driven
 * heartbeat from such a tab arrives every hundred seconds or not at all — a liveness check built
 * on one would kill exactly the socket of a player who has not enabled audio yet. The browser's
 * own network stack answers a protocol ping without waking a single timer, so this measures the
 * *socket* rather than the tab's scheduler. A half-open socket left by a killed tab is otherwise
 * invisible until TCP notices, which can be minutes.
 */
export const AUDIO_LIVENESS_INTERVAL_MS = 30_000;

/**
 * Concurrent sockets one player may hold.
 *
 * A player reloads. The old socket usually closes cleanly, but a killed tab, a suspended
 * laptop or a dropped mobile connection leaves a half-open one that survives until the liveness
 * check above notices — so "fifty reloads" really can mean fifty entries for a minute at a
 * time. Three is room for a phone, a desktop and one zombie.
 *
 * OVER THE CAP THE OLDEST IS EVICTED, not the newest refused — the opposite of what
 * {@link import('./link').LINK_MAX_PENDING} does, and for the opposite reason. A pending link
 * code is a credential somebody is about to type, so dropping it is invisible to them and
 * silently breaks the thing they are doing. A socket over this cap is almost always the tab
 * they just reloaded away from; refusing the NEW one would mean a player whose audio never
 * comes back until a timeout they cannot see expires. The evicted socket is told
 * `superseded` so its client does not treat it as a network fault and reconnect into a loop.
 */
export const AUDIO_MAX_SOCKETS_PER_PLAYER = 3;

/**
 * Ceiling on live sockets across every player and the game server.
 *
 * Refused rather than evicted (there is no "oldest" that is safe to drop across players), with
 * a code the client backs off on. Sized so the memory is bounded and forgettable; a deployment
 * that needs more has outgrown one sidecar process, which is the decision `docs/…-plan.md` §4
 * says to revisit rather than tune.
 */
export const AUDIO_MAX_SOCKETS = 1_024;

/**
 * Cap on one inbound frame FROM A BROWSER.
 *
 * Every message a tab may send is a ping: a type, an integer and a timestamp. Two kilobytes is
 * already a hundredfold headroom, and the cap is what stops an authenticated-but-broken peer
 * deciding how much the sidecar allocates. `ws`'s `maxPayload` enforces it at the framing layer,
 * before a buffer exists — on the browsers' own `WebSocketServer`, see {@link AUDIO_MAX_GAME_FRAME_BYTES}.
 */
export const AUDIO_MAX_FRAME_BYTES = 2 * 1024;

/**
 * Cap on one inbound frame FROM THE GAME SERVER, which is the one peer with something large to say:
 * a `catalog` is every area and cue the server configured, and the shipped defaults alone are several
 * kilobytes (plan §9.3 "Wire rules"). Mirrored as `AudioWire.MAX_SERVER_FRAME_BYTES`; the mod refuses
 * to send a catalog over it and says so in its log, rather than having this end close the socket.
 *
 * TWO CAPS, TWO SERVERS. `ws` applies `maxPayload` per `WebSocketServer`, so the sidecar upgrades a
 * game-server handshake on one server and a browser handshake on another, AFTER the credential has
 * said which it is — a browser never gets the larger allowance, not even for one frame.
 */
export const AUDIO_MAX_SERVER_FRAME_BYTES = 64 * 1024;

/** The longest id the wire accepts — area, cue, track, district, dimension, biome, ride or instance. */
export const AUDIO_MAX_ID_LENGTH = 128;

/** The longest area display name (MiniMessage source), in characters. */
export const AUDIO_MAX_DISPLAY_NAME_LENGTH = 512;

/**
 * Bounds on one catalog. Over them the extra entries are dropped and reported, never the catalog. The
 * mod refuses an `audio.yml` past the same numbers at load (`AudioConfig.MAX_AREAS`, `MAX_CUES`,
 * `MAX_TRACKS_PER_AREA`), so in practice nothing is ever dropped here.
 */
export const AUDIO_MAX_AREAS = 128;
export const AUDIO_MAX_CUES = 64;
export const AUDIO_MAX_AREA_TRACKS = 32;

/**
 * Most names one trigger may list — the biomes of a `biome` trigger, the rides of a `ride` one. The mod
 * refuses an `audio.yml` past the same number (`AudioConfig.MAX_TRIGGER_NAMES`); an area over it here is
 * dropped and reported, like any other area this page cannot read. (A `battle` trigger keeps its own,
 * smaller bound: there are only so many kinds of battle.)
 */
export const AUDIO_MAX_TRIGGER_NAMES = 64;

/** The longest crossfade an area may ask for. Thirty seconds is already a mood, not a fade. */
export const AUDIO_MAX_FADE_MS = 30_000;

/** Live shared playbacks the sidecar holds, and members per playback (§9.9). */
export const AUDIO_MAX_INSTANCES = 256;
export const AUDIO_MAX_INSTANCE_PLAYERS = 512;

/**
 * How long a shared playback's TIMELINE outlives the game server's socket (§9.9).
 *
 * When the server socket closes the sidecar marks every player offline at once (plan §9.3 "Wire
 * rules": the mod's `leave` frames at shutdown may never make it out) — so contexts are not kept.
 * What IS kept, for this long, is each instance's `startedAtMs`: the mod re-sends every live instance
 * after its next hello, and an instance it re-sends inside this window resumes on the beat it was on
 * rather than restarting for everyone. After it, the id is forgotten and a re-send is a new start.
 * Evicted lazily, on the next frame that looks — never by a timer.
 */
export const AUDIO_INSTANCE_GRACE_MS = 15_000;

/**
 * How long the TAB lets music linger after being told its player went offline, before fading out.
 * Long enough to swallow the offline-then-online pair a game-server blip produces (the mod redials in
 * well under this and re-sends the context), so a blip is inaudible; short enough that leaving the
 * server still sounds like leaving it.
 */
export const AUDIO_OFFLINE_LINGER_MS = 4_000;

// ----------------------------------------------------------------------------- backoff

/**
 * RECONNECT IS A POLICY, NOT A NUMBER, AND BOTH ENDS FOLLOW THE SAME ONE.
 *
 * The mod mirrors these three constants in `AudioBackoff`, where the reasoning is written out at
 * length. In short: a sidecar restart drops EVERY socket it holds at the same instant, so without
 * jitter every client computes the same delay from the same attempt number and they all come back
 * as one wave — aimed at a process that is still opening its database pool, which fails them all
 * and schedules the next synchronised wave. The clients here are a fleet of game servers plus every
 * open browser tab, and they were disconnected by one event rather than independently, which is
 * what makes the herd real rather than theoretical.
 *
 * EQUAL jitter rather than full jitter: the draw is over the UPPER HALF of the ceiling. Full jitter
 * decorrelates marginally better but can return nearly zero, and a nearly-zero retry against a
 * sidecar that is still booting is a busy loop aimed at the thing we are waiting for.
 */
export const AUDIO_RETRY_BASE_MS = 500;

/** The ceiling. A minute: unattended recovery without a client that costs anything while it waits. */
export const AUDIO_RETRY_MAX_MS = 60_000;

/** Highest attempt the doubling is computed for; `500 << 7` is already over the cap. */
export const AUDIO_RETRY_MAX_SHIFT = 7;

/** The un-jittered ceiling for `attempt` — the widest a jittered delay can be. */
export function audioRetryCeilingMs(attempt: number): number {
  const clamped = Math.min(Math.max(Math.trunc(attempt), 0), AUDIO_RETRY_MAX_SHIFT);
  return Math.min(AUDIO_RETRY_BASE_MS * 2 ** clamped, AUDIO_RETRY_MAX_MS);
}

/**
 * The delay before `attempt` (0 = the first retry after a working socket died), jittered.
 *
 * `random` is injected so a test can assert the bounds deterministically, and so it is obvious that
 * two tabs draw independently — which is the entire point.
 */
export function audioRetryDelayMs(attempt: number, random: () => number = Math.random): number {
  const ceiling = audioRetryCeilingMs(attempt);
  const half = ceiling / 2;
  return Math.round(half + random() * half);
}

// ------------------------------------------------------------------------------ guards

/**
 * Narrows an already-parsed frame the SIDECAR received. Strict, exactly as
 * `parseClaim` is strict and for the same reason: this comes from a browser, where an
 * unexpected shape is a bug worth reporting rather than forward compatibility.
 */
export function isAudioClientMessage(value: unknown): value is AudioClientMessage {
  if (typeof value !== 'object' || value === null) return false;
  const m = value as Record<string, unknown>;
  if (m['type'] !== 'ping') return false;
  return Number.isFinite(m['id']) && Number.isFinite(m['clientTimeMs']);
}

/**
 * Narrows a frame a CLIENT received. LENIENT about types it does not know — an unrecognised
 * `type` returns false and the caller drops it — which is what made adding the §9 frames safe for a
 * tab that predates them.
 *
 * A `catalog` is checked only for its envelope here: its areas are normalised by
 * {@link normaliseAudioCatalog}, which drops what it cannot read entry by entry rather than throwing
 * the whole catalog away for one bad area.
 */
export function isAudioServerMessage(value: unknown): value is AudioServerMessage {
  if (typeof value !== 'object' || value === null) return false;
  const m = value as Record<string, unknown>;
  switch (m['type']) {
    case 'hello':
      return (
        Number.isFinite(m['version']) &&
        (m['peer'] === 'player' || m['peer'] === 'server') &&
        Number.isFinite(m['serverTimeMs']) &&
        Number.isFinite(m['pingIntervalMs']) &&
        (m['peer'] === 'server' ? m['playerUuid'] === undefined : isUuidText(m['playerUuid']))
      );
    case 'pong':
      return Number.isFinite(m['id']) && Number.isFinite(m['clientTimeMs']) && Number.isFinite(m['serverTimeMs']);
    case 'refused':
      return typeof m['code'] === 'string' && Number.isFinite(m['serverVersion']);
    case 'catalog':
      return isCatalogRevision(m['revision']) && Array.isArray(m['areas']) && Array.isArray(m['cues']);
    case 'context':
      return (
        typeof m['online'] === 'boolean' &&
        Number.isFinite(m['serverTimeMs']) &&
        (m['context'] === null || normalisePlayerAudioContext(m['context']) !== null) &&
        (m['instance'] === undefined || normaliseInstanceRef(m['instance']) !== null)
      );
    case 'cue':
      return isAudioId(m['cue']) && Number.isFinite(m['serverTimeMs']);
    default:
      return false;
  }
}

// ----------------------------------------------------------------- the §9 normalisers

/**
 * The id charset every §9 identifier shares — area, cue, track, district, dimension, biome, ride, instance.
 *
 * Wide enough for what the game actually names (`minecraft:overworld`, `boss:<entity uuid>`,
 * `voice/en/npc_greeting`) and narrow enough that an id can be a cache key, a log field and a
 * `data-` attribute without escaping: no space, no quote, no angle bracket.
 */
const AUDIO_ID = /^[A-Za-z0-9_.:/-]+$/;

/**
 * A catalog revision: the mod's 16 hex characters, or any other short token a future mod spells it as.
 * Compared for equality and nothing else, so the only shape rule is that it is a usable string.
 */
export function isCatalogRevision(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_.:-]{1,64}$/.test(value);
}

export function isAudioId(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= AUDIO_MAX_ID_LENGTH && AUDIO_ID.test(value);
}

/**
 * A language tag as the wire carries it, normalised: lower case, `_` read as `-` (Minecraft spells a
 * locale `en_us`, the web `en-US`). Null for anything that is not one.
 *
 * NORMALISED, NOT NEGOTIATED. `en-gb` and `en` stay different tags: §9.8 says a voiced line is played
 * in exactly the language the server's text is in, and a missing clip is silent rather than
 * substituted, so there is deliberately no "close enough" step anywhere in this package.
 */
export function normaliseAudioLang(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const tag = value.trim().replace(/_/g, '-').toLowerCase();
  return /^[a-z]{2,8}(-[a-z0-9]{1,8})*$/.test(tag) && tag.length <= 35 ? tag : null;
}

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

/**
 * A list of ids a trigger names, read the way `battles` always has been: absent (or null) is the empty list,
 * and anything else must be a list of at most `max` ids, every one of them usable — or the trigger is not one
 * this page can match. Kept in the order the mod wrote it, duplicates and all, so the relay forwards what it
 * was sent.
 */
function triggerNames(value: unknown, max: number): string[] | null {
  const raw = value ?? [];
  if (!Array.isArray(raw) || raw.length > max || !raw.every(isAudioId)) return null;
  return raw as string[];
}

/** One of `allowed`, absent (or null) as undefined, or `false` for a value this generation does not define. */
function optionalOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined | false {
  if (value === undefined || value === null) return undefined;
  return (allowed as readonly unknown[]).includes(value) ? (value as T) : false;
}

/** One trigger, or null when it is not one this generation can match. */
export function normaliseAreaTrigger(value: unknown): AreaTrigger | null {
  if (typeof value !== 'object' || value === null) return null;
  const t = value as Record<string, unknown>;
  switch (t['kind']) {
    case 'default':
      return { kind: 'default' };
    case 'base':
      return { kind: 'base' };
    case 'dimension':
    case 'district':
      return isAudioId(t['id']) ? { kind: t['kind'], id: t['id'] } : null;
    case 'battle': {
      const battles = triggerNames(t['battles'], BATTLE_KINDS.length * 4);
      return battles === null ? null : { kind: 'battle', battles };
    }
    case 'biome': {
      // An empty list is kept and never matches (see AreaTrigger): the mod refuses one, so it is not worth a
      // second rule here, and the relay forwards the shape it was sent.
      const biomes = triggerNames(t['biomes'], AUDIO_MAX_TRIGGER_NAMES);
      return biomes === null ? null : { kind: 'biome', biomes };
    }
    case 'ride': {
      const rides = triggerNames(t['rides'], AUDIO_MAX_TRIGGER_NAMES);
      const motion = optionalOf(t['motion'], RIDE_MOTIONS);
      const water = optionalOf(t['water'], RIDE_WATERS);
      // A motion or water this build has never heard of is refused, not read as "either": a ride sound that
      // played in every state because its condition was not understood is the wrong failure.
      if (rides === null || motion === false || water === false) return null;
      return { kind: 'ride', rides, ...(motion === undefined ? {} : { motion }), ...(water === undefined ? {} : { water }) };
    }
    default:
      // A kind a newer mod knows (`region`, `weather`) is unmatchable here, so the area is dropped and
      // REPORTED rather than guessed at: an area that played everywhere because its trigger was not
      // understood would be worse than one that waits for this build to learn it.
      return null;
  }
}

/** One area, or a sentence saying why it was dropped. Clamps what has a range rather than refusing it. */
export function normaliseAreaDef(value: unknown): AreaDef | string {
  if (typeof value !== 'object' || value === null) return 'an area is not an object';
  const a = value as Record<string, unknown>;
  const id = a['id'];
  if (!isAudioId(id)) return 'an area has no usable id';
  const trigger = normaliseAreaTrigger(a['trigger']);
  if (trigger === null) return `area ${id}: its trigger is not one this page can match`;
  const category = a['category'];
  if (!(AREA_CATEGORIES as readonly unknown[]).includes(category)) return `area ${id}: category must be one of ${AREA_CATEGORIES.join(', ')}`;
  const order = a['order'] ?? 'loop';
  if (!(AREA_ORDERS as readonly unknown[]).includes(order)) return `area ${id}: order must be one of ${AREA_ORDERS.join(', ')}`;
  const tracks = a['tracks'];
  if (!Array.isArray(tracks) || tracks.length > AUDIO_MAX_AREA_TRACKS || !tracks.every(isAudioId)) {
    return `area ${id}: tracks must be a list of at most ${AUDIO_MAX_AREA_TRACKS} track ids`;
  }
  const displayName = typeof a['displayName'] === 'string' ? a['displayName'].slice(0, AUDIO_MAX_DISPLAY_NAME_LENGTH) : id;
  return {
    id,
    displayName,
    category: category as AreaCategory,
    priority: finiteOr(a['priority'], 0),
    trigger,
    tracks: tracks as string[],
    fadeMs: clamp(Math.round(finiteOr(a['fadeMs'], 1_000)), 0, AUDIO_MAX_FADE_MS),
    volume: clamp(finiteOr(a['volume'], 1), 0, 1),
    order: order as AreaOrder,
  };
}

/**
 * One cue, or a sentence saying why it was dropped. A `category` this build does not know drops the cue, as an
 * unknown area category drops the area: playing it under the wrong slider would be a guess.
 */
export function normaliseCueDef(value: unknown): CueDef | string {
  if (typeof value !== 'object' || value === null) return 'a cue is not an object';
  const c = value as Record<string, unknown>;
  if (!isAudioId(c['id'])) return 'a cue has no usable id';
  if (!isAudioId(c['track'])) return `cue ${c['id']}: track must be a track id`;
  const category = optionalOf(c['category'], CUE_CATEGORIES);
  if (category === false) return `cue ${c['id']}: category must be one of ${CUE_CATEGORIES.join(', ')}`;
  return { id: c['id'], track: c['track'], volume: clamp(finiteOr(c['volume'], 1), 0, 1), ...(category === undefined ? {} : { category }) };
}

/** The slider a cue plays under: its own `category`, or `alerts` when it names none. */
export function cueCategoryOf(cue: Pick<CueDef, 'category'>): CueCategory {
  return cue.category ?? 'alerts';
}

export interface NormalisedCatalog {
  /** Null only when the envelope itself is unreadable — no revision, no lists. */
  readonly catalog: AudioCatalog | null;
  /** One sentence per dropped entry, for the one log line (sidecar) or console note (tab) that reports it. */
  readonly dropped: readonly string[];
}

/**
 * The catalog, keeping every area and cue it can read and naming each one it cannot.
 *
 * PER ENTRY, NOT ALL OR NOTHING — the stance §15 takes for a config key: one area a newer mod wrote
 * with a trigger this build does not know must not silence every other area on the server. Duplicate
 * ids keep the FIRST, because catalog order is the tie-break §9.4 resolves with and a later duplicate
 * could only ever lose to it. `lang` defaults to `en`, the server's text language today (§9.8).
 */
export function normaliseAudioCatalog(value: unknown): NormalisedCatalog {
  if (typeof value !== 'object' || value === null) return { catalog: null, dropped: ['the catalog is not an object'] };
  const c = value as Record<string, unknown>;
  const revision = c['revision'];
  if (!isCatalogRevision(revision) || !Array.isArray(c['areas']) || !Array.isArray(c['cues'])) {
    return { catalog: null, dropped: ['a catalog needs a revision string and lists of areas and cues'] };
  }
  const dropped: string[] = [];
  const areas: AreaDef[] = [];
  const seenAreas = new Set<string>();
  for (const raw of c['areas'] as unknown[]) {
    if (areas.length >= AUDIO_MAX_AREAS) {
      dropped.push(`more than ${AUDIO_MAX_AREAS} areas; the rest were dropped`);
      break;
    }
    const area = normaliseAreaDef(raw);
    if (typeof area === 'string') dropped.push(area);
    else if (seenAreas.has(area.id)) dropped.push(`area ${area.id}: a second area with the same id`);
    else {
      seenAreas.add(area.id);
      areas.push(area);
    }
  }
  const cues: CueDef[] = [];
  const seenCues = new Set<string>();
  for (const raw of c['cues'] as unknown[]) {
    if (cues.length >= AUDIO_MAX_CUES) {
      dropped.push(`more than ${AUDIO_MAX_CUES} cues; the rest were dropped`);
      break;
    }
    const cue = normaliseCueDef(raw);
    if (typeof cue === 'string') dropped.push(cue);
    else if (seenCues.has(cue.id)) dropped.push(`cue ${cue.id}: a second cue with the same id`);
    else {
      seenCues.add(cue.id);
      cues.push(cue);
    }
  }
  const lang = c['lang'] === undefined ? 'en' : normaliseAudioLang(c['lang']);
  if (lang === null) dropped.push(`lang ${JSON.stringify(c['lang'])} is not a language tag; reading it as en`);
  return { catalog: { revision, lang: lang ?? 'en', areas, cues }, dropped };
}

/**
 * A context, normalised; null when the one required field is missing. Unknown fields are dropped, and so is
 * an optional field that is not readable — a district that is not an id is no district — EXCEPT a ride's id:
 * a `ride` that is an object says the player sits on a ride, and only WHICH ride is unreadable, so it reads as
 * the ride the mod cannot name (`""`) rather than as no ride at all. A ride's two flags count only as `true`.
 */
export function normalisePlayerAudioContext(value: unknown): PlayerAudioContext | null {
  if (typeof value !== 'object' || value === null) return null;
  const c = value as Record<string, unknown>;
  if (!isAudioId(c['dimension'])) return null;
  const district = isAudioId(c['district']) ? c['district'] : undefined;
  const battle = isAudioId(c['battle']) ? c['battle'] : undefined;
  const lang = normaliseAudioLang(c['lang']) ?? undefined;
  const biome = isAudioId(c['biome']) ? c['biome'] : undefined;
  const ride = normaliseAudioRide(c['ride']);
  return {
    dimension: c['dimension'],
    ...(district === undefined ? {} : { district }),
    ...(c['base'] === true ? { base: true } : {}),
    ...(battle === undefined ? {} : { battle }),
    ...(lang === undefined ? {} : { lang }),
    ...(biome === undefined ? {} : { biome }),
    ...(ride === undefined ? {} : { ride }),
  };
}

/** A context's `ride`, normalised; undefined when it is not an object (no ride). See {@link normalisePlayerAudioContext}. */
function normaliseAudioRide(value: unknown): AudioRide | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
  const r = value as Record<string, unknown>;
  const id = isAudioId(r['id']) ? r['id'] : '';
  return { id, ...(r['moving'] === true ? { moving: true } : {}), ...(r['water'] === true ? { water: true } : {}) };
}

export function normaliseInstanceRef(value: unknown): AudioInstanceRef | null {
  if (typeof value !== 'object' || value === null) return null;
  const i = value as Record<string, unknown>;
  if (!isAudioId(i['id']) || !isAudioId(i['area']) || typeof i['startedAtMs'] !== 'number' || !Number.isFinite(i['startedAtMs'])) return null;
  return { id: i['id'], area: i['area'], startedAtMs: i['startedAtMs'] };
}

/**
 * A player uuid as every map here is keyed: lower case. The mod writes `java.util.UUID.toString()`, which
 * is lower case already, and the code request a session was minted from came from the same place; this
 * makes the routing independent of that staying true.
 */
export function routingUuid(value: string): UuidText {
  return value.toLowerCase() as UuidText;
}

/** What {@link parseAudioGameFrame} decided about one frame from the game server. */
export type AudioGameFrameParse =
  | { readonly kind: 'frame'; readonly frame: AudioGameFrame; readonly dropped: readonly string[] }
  /** A `type` this generation does not define. Ignored: the mod may be a newer build (see `handleFrame`). */
  | { readonly kind: 'unknown'; readonly type: string }
  /** A type this generation defines, in a shape it cannot read. Dropped and logged; the socket stays open. */
  | { readonly kind: 'malformed'; readonly problem: string };

/**
 * Reads one already-JSON-parsed frame from the GAME SERVER.
 *
 * STRICT ABOUT WHAT A FRAME NAMES, LENIENT ABOUT WHAT IT ADDS. A `context` for something that is not a
 * uuid, or an `instance` whose member list is not a list of uuids, is malformed — those fields are
 * the routing, and a routing guess is how one player's battle theme reaches another. A field nobody
 * here reads is ignored, because the mod is allowed to be newer than this sidecar.
 */
export function parseAudioGameFrame(value: unknown): AudioGameFrameParse {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return { kind: 'malformed', problem: 'a frame must be a JSON object' };
  const f = value as Record<string, unknown>;
  const type = f['type'];
  switch (type) {
    case 'catalog': {
      const { catalog, dropped } = normaliseAudioCatalog(f);
      return catalog === null ? { kind: 'malformed', problem: dropped.join('; ') } : { kind: 'frame', frame: { type: 'catalog', ...catalog }, dropped };
    }
    case 'context': {
      if (!isUuidText(f['player'])) return { kind: 'malformed', problem: 'context.player must be a canonical uuid' };
      const context = normalisePlayerAudioContext(f['context']);
      if (context === null) return { kind: 'malformed', problem: 'context.context needs at least a dimension id' };
      return { kind: 'frame', frame: { type: 'context', player: routingUuid(f['player']), context }, dropped: [] };
    }
    case 'cue':
      if (!isUuidText(f['player'])) return { kind: 'malformed', problem: 'cue.player must be a canonical uuid' };
      if (!isAudioId(f['cue'])) return { kind: 'malformed', problem: 'cue.cue must be a cue id' };
      return { kind: 'frame', frame: { type: 'cue', player: routingUuid(f['player']), cue: f['cue'] }, dropped: [] };
    case 'leave':
      if (!isUuidText(f['player'])) return { kind: 'malformed', problem: 'leave.player must be a canonical uuid' };
      return { kind: 'frame', frame: { type: 'leave', player: routingUuid(f['player']) }, dropped: [] };
    case 'instance': {
      if (!isAudioId(f['id']) || !isAudioId(f['area'])) return { kind: 'malformed', problem: 'instance needs an id and an area id' };
      const players = f['players'];
      if (!Array.isArray(players) || players.length > AUDIO_MAX_INSTANCE_PLAYERS || !players.every(isUuidText)) {
        return { kind: 'malformed', problem: `instance.players must be a list of at most ${AUDIO_MAX_INSTANCE_PLAYERS} canonical uuids` };
      }
      const unique = [...new Set((players as UuidText[]).map(routingUuid))];
      return { kind: 'frame', frame: { type: 'instance', id: f['id'], area: f['area'], players: unique }, dropped: [] };
    }
    case 'instance_end':
      if (!isAudioId(f['id'])) return { kind: 'malformed', problem: 'instance_end needs an id' };
      return { kind: 'frame', frame: { type: 'instance_end', id: f['id'] }, dropped: [] };
    default:
      return { kind: 'unknown', type: typeof type === 'string' ? type.slice(0, 64) : String(type) };
  }
}

// ------------------------------------------------------------------- the way in (§9.2)

/**
 * `POST /v1/audio/codes` — the game server asks for a one-time code for a player who typed `/audio`.
 * Authenticated with the shared `serverToken`, exactly as `POST /v1/link` is.
 *
 * `skin` is OPTIONAL and the mod decides whether to send it: the hex hash of the player's skin on
 * `textures.minecraft.net`, which the page draws the player's head from. Whether a player's browser
 * may ask Mojang's texture host for it is the server operator's privacy decision, not the page's —
 * without it the page draws a head from the uuid and asks nobody anything.
 */
export interface AudioCodeRequest {
  readonly uuid: UuidText;
  readonly name: string;
  readonly skin?: string;
}

/** The answer: the code, the whole link to put in `linkMessage`'s `<url>`, and its expiry (epoch ms). */
export interface AudioCodeIssued {
  readonly code: string;
  readonly url: string;
  readonly expiresAt: number;
}

/** The player a claim or a renewal is bound to — display only, off the sidecar's own record. */
export interface AudioPlayer {
  readonly uuid: UuidText;
  readonly name: string;
  readonly skin: string | null;
}

/** `POST /v1/audio/claim` body. A code and nothing else: the player comes off the sidecar's record. */
export interface AudioClaimRequest {
  readonly code: string;
}

/**
 * `POST /v1/audio/claim` → 200. `token` is the player-tier session (§9.2.3) for the rest of the SPA,
 * fifteen minutes like every session; the renewal that outlives it travels as an `httpOnly` cookie and
 * is deliberately absent from this body.
 */
export interface AudioClaimAccepted {
  readonly player: AudioPlayer;
  readonly token: string;
  readonly tokenExpiresAt: number;
  readonly renewalExpiresAt: number;
}

/**
 * `POST /v1/audio/session` → 200: a fresh, short-lived socket credential minted from the renewal
 * cookie (or from a sign-in that names a player, which also establishes one). Called before every dial,
 * and by the phone whenever its player session is about to lapse.
 */
export interface AudioSessionIssued {
  readonly player: AudioPlayer;
  readonly ticket: string;
  readonly ticketExpiresAt: number;
  /**
   * A fresh fifteen-minute PLAYER session for the same player — what Stats reads its own rows with — so
   * the phone stays signed in as long as the renewal lives, not for fifteen minutes after the claim.
   * Optional because the page and the sidecar ship separately: a sidecar older than this field answers
   * without it, and the page then simply has no player session to show Stats with.
   */
  readonly token?: string;
  readonly tokenExpiresAt?: number;
  readonly renewalExpiresAt: number;
}

export type AudioWayInErrorCode =
  | 'unauthorised'
  | 'invalid_request'
  | 'payload_too_large'
  | 'unknown_code'
  | 'too_many_pending'
  | 'no_renewal'
  | 'forbidden_tier'
  /** A staff sign-in's `/link` binding could not be looked up just now; not "unlinked". */
  | 'binding_unavailable'
  | 'rate_limited';

/** The sidecar route prefix of the way in. The socket itself is {@link AUDIO_PATH}, exactly. */
export const AUDIO_CODES_PATH = '/v1/audio/codes';
export const AUDIO_CLAIM_PATH = '/v1/audio/claim';
export const AUDIO_SESSION_PATH = '/v1/audio/session';

/** The fragment key the code rides in: `/audio#code=…`, so it reaches no access log (§9.2.2). */
export const AUDIO_CODE_FRAGMENT_KEY = 'code';

/** The page the link opens. */
export const AUDIO_PAGE_PATH = '/audio';

/** Builds the link `/audio` prints: `<web origin>/audio#code=<code>`. */
export function audioLinkUrl(publicOrigin: string, code: string): string {
  return `${publicOrigin.replace(/\/+$/, '')}${AUDIO_PAGE_PATH}#${AUDIO_CODE_FRAGMENT_KEY}=${encodeURIComponent(code)}`;
}

/** Random bytes in a code: 24 is 192 bits, comfortably over §9.2.2's floor of 128. */
export const AUDIO_CODE_BYTES = 24;

/** A code as the sidecar mints it: base64url, no padding, exactly {@link AUDIO_CODE_BYTES} bytes' worth. */
export function isAudioCode(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{32}$/.test(value);
}

/** How long a code lives. "A few minutes" (§9.2.2): long enough to alt-tab, short enough to be worthless in a screenshot. */
export const AUDIO_CODE_TTL_SECONDS = 5 * 60;

/** Pending codes the sidecar holds at once. Over it a push is refused (429), never another player's code evicted. */
export const AUDIO_MAX_PENDING_CODES = 512;

/** Largest `/v1/audio/codes` or `/v1/audio/claim` body. */
export const AUDIO_WAY_IN_MAX_BYTES = 1024;

/**
 * A socket ticket's life. Long enough to dial and no longer: the socket checks its credential once, at
 * the handshake, so a ticket only has to survive the round trip between `/session` and the upgrade.
 */
export const AUDIO_TICKET_TTL_SECONDS = 2 * 60;

/**
 * The renewal's life, SLIDING: every `/session` it answers re-issues it for this long again, so a
 * player who listens every week never runs `/audio` twice on the same device (§9.2.4)…
 */
export const AUDIO_RENEWAL_TTL_SECONDS = 30 * 24 * 60 * 60;

/** …up to this hard ceiling from the claim, after which `/audio` in game is asked for again. */
export const AUDIO_RENEWAL_MAX_SECONDS = 90 * 24 * 60 * 60;

/** The renewal cookie. `httpOnly`, scoped to {@link AUDIO_SESSION_PATH}, so no script and no other route ever sees it. */
export const AUDIO_RENEWAL_COOKIE = 'dc_audio_renewal';

/**
 * The audiences that keep the three tokens apart — each is the session audience plus a suffix, so a
 * deployment that configured one audience has configured all three.
 *
 * * the SESSION (`aud` = the configured audience) is what the read plane accepts;
 * * a TICKET opens an audio socket and nothing else — the read plane checks for the bare audience,
 *   so a ticket is refused there by construction rather than by a rule somebody has to remember;
 * * a RENEWAL mints tickets and nothing else — not even a socket accepts it.
 */
export function audioTicketAudience(sessionAudience: string): string {
  return `${sessionAudience}:audio-ticket`;
}
export function audioRenewalAudience(sessionAudience: string): string {
  return `${sessionAudience}:audio-renewal`;
}

/**
 * The `sub` of a session or ticket minted from an in-game `/audio` claim: `minecraft:<uuid>`.
 *
 * NEVER A SNOWFLAKE, ON PURPOSE. `sub` is a Discord account id everywhere else, and the `/link` claim
 * binds `sub` as one — so a subject that cannot be mistaken for a snowflake is what keeps an audio
 * session from ever being bound as a Discord account (`link.ts` refuses it, `isDiscordId` says why).
 */
export function audioSubject(playerUuid: string): string {
  return `minecraft:${playerUuid}`;
}

/**
 * A skin texture hash as `textures.minecraft.net` names one: lower-case hex, at most 128 characters —
 * the mod's own shape (`SkinHashes`, `EditorHttpClient.SKIN`), so the two ends agree on what a hash is.
 */
export function isSkinHash(value: unknown): value is string {
  return typeof value === 'string' && /^[0-9a-f]{1,128}$/.test(value);
}

/**
 * A Minecraft name as the page will print it: one to forty printable characters. Wider than vanilla's
 * sixteen word characters on purpose — a Bedrock bridge prefixes names (`.Steve`, `*Alex`) — and only
 * ever displayed, never matched against anything.
 */
export function isAudioPlayerName(value: unknown): value is string {
  return typeof value === 'string' && value.length >= 1 && value.length <= 40 && /^[^\u0000-\u001f\u007f<>"'`]+$/.test(value);
}

/**
 * The name as the page will print it, out of whatever the game server sent: control characters and
 * markup characters dropped, trimmed, at most forty characters. Null when nothing printable is left.
 *
 * LENIENT, BECAUSE IT IS COSMETIC. The name only ever labels the player's own page, and the server that
 * sent it is authenticated; refusing the whole code over a name the mod considers fine (it allows 64
 * characters) would cost the player their link over a label.
 */
export function sanitiseAudioPlayerName(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const printable = value.replace(/[\u0000-\u001f\u007f<>"'`]/g, '').trim().slice(0, 40).trim();
  return isAudioPlayerName(printable) ? printable : null;
}
